import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Modal, Space, Spin, Table, Typography, message } from 'antd';
import { BranchesOutlined, CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  GitControlPanelModal,
  GitRepoBrowser,
  GitSessionRegistryProvider,
  useGitDraftLeaveGuard,
  useGitSessionRegistryOptional,
  type GitRepoBrowserHandle,
} from '@modeling/git-file-viewer';
import { GitCredentialsSetup } from '../../components/git/GitCredentialsSetup';
import { SchemaGitConnectionForm } from '../../components/schema/SchemaGitConnectionForm';
import { ensureGitFileViewerReady } from '../../bootstrap/gitFileViewer';
import { SCHEMA_PROFILES_DIR } from '../../config/schemaGit';
import { useDeferredModule } from '../../hooks/useDeferredModule';
import { useGitAuth } from '../../hooks/useGitAuth';
import { useSchemaGitSettings } from '../../hooks/useSchemaGitSettings';
import { schemaProfileService } from '../../api/services';
import { resolveGitApiBaseUrl } from '../../api/env';
import { isGitCredentialNotConfiguredError } from '../../utils/gitErrors';
import type { SchemaGitConnection } from '../../api/schemaProfileApi';
import type { SchemaProfile } from '../../types/schemaProfile';
import type { SchemaReloadResult } from '../../api/schemaProfileApi';
import './SchemaProfilesPage.css';

const { Text } = Typography;

function profileGitPath(profileId: string): string {
  return `${SCHEMA_PROFILES_DIR}/${profileId}/`;
}

function confirmDiscardDraft(): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: '无法保存草稿',
      content: '网络或冲突导致暂存失败。放弃更改并离开？',
      okText: '放弃更改',
      cancelText: '取消',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

function SchemaProfileEditor({
  profileId,
  repoUrl,
  defaultBranch,
  onCredentialRequired,
  browserRef,
  onDirtyChange,
}: {
  profileId: string;
  repoUrl: string;
  defaultBranch: string;
  onCredentialRequired: () => void;
  browserRef: RefObject<GitRepoBrowserHandle | null>;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const baseURL = resolveGitApiBaseUrl();
  const { getToken, commitAuthor } = useGitAuth();
  const registry = useGitSessionRegistryOptional();

  const session = useMemo(
    () => registry?.sessions.find((s) => s.repoUrl === repoUrl) ?? null,
    [registry?.sessions, repoUrl],
  );
  const currentBranch = session?.branch ?? defaultBranch;
  const changeCount = useMemo(() => {
    if (!session) return 0;
    const staged = session.changes?.staged?.length ?? 0;
    const unstaged = session.changes?.unstaged?.length ?? 0;
    const conflicts = session.rebase?.conflictFiles?.length ?? 0;
    return staged + unstaged + conflicts;
  }, [session]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deployLoading, setDeployLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<SchemaReloadResult | null>(null);

  const runReload = useCallback(
    async (dryRun: boolean) => {
      if (!repoUrl) {
        message.error('请填写 Git 仓库 URL');
        return null;
      }
      if (!dryRun && changeCount > 0) {
        message.warning('请先通过「源代码管理」提交并同步变更，再执行物化部署');
        return null;
      }
      return schemaProfileService.reload({
        repoUrl,
        schemaProfile: profileId,
        branch: currentBranch,
        dryRun,
      });
    },
    [changeCount, currentBranch, profileId, repoUrl],
  );

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const result = await runReload(true);
      if (!result) return;
      setPreviewResult(result);
      setPreviewOpen(true);
    } catch (err) {
      if (isGitCredentialNotConfiguredError(err)) {
        onCredentialRequired();
        return;
      }
      message.error(err instanceof Error ? err.message : '预览物化失败');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeploy = async () => {
    setDeployLoading(true);
    try {
      const result = await runReload(false);
      if (!result) return;
      message.success(`物化完成：载入 ${result.loaded ?? result.count} 个 Schema key`);
      setPreviewOpen(false);
      setPreviewResult(null);
    } catch (err) {
      if (isGitCredentialNotConfiguredError(err)) {
        onCredentialRequired();
        return;
      }
      message.error(err instanceof Error ? err.message : '物化部署失败');
    } finally {
      setDeployLoading(false);
    }
  };

  if (!repoUrl || !baseURL) {
    return (
      <div className="schema-profiles-editor-placeholder">
        请填写 Git 仓库 URL，并配置 <code>REACT_APP_GIT_API_BASE_URL</code>
      </div>
    );
  }

  return (
    <div className="schema-profiles-editor">
      <div className="schema-profiles-editor-toolbar">
        <Space wrap>
          <Text type="secondary">
            Profile: <code>{profileId}</code>
          </Text>
          <Text type="secondary">
            分支: <code>{currentBranch}</code>
          </Text>
          <GitControlPanelModal
            author={commitAuthor}
            repoUrl={repoUrl}
            branch={currentBranch}
            renderTrigger={({ onClick, changeCount: scmCount }) => (
              <Badge count={scmCount} size="small" offset={[-2, 0]}>
                <Button size="small" icon={<BranchesOutlined />} onClick={onClick}>
                  源代码管理
                </Button>
              </Badge>
            )}
          />
          <Button size="small" loading={previewLoading} onClick={handlePreview}>
            预览物化
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<CloudUploadOutlined />}
            loading={deployLoading}
            onClick={handleDeploy}
          >
            部署物化
          </Button>
        </Space>
        <Button type="link" size="small" onClick={onCredentialRequired}>
          Git 凭据
        </Button>
      </div>

      <div className="schema-profiles-editor-browser git-file-viewer-theme-root">
        <GitRepoBrowser
          ref={browserRef as React.ComponentProps<typeof GitRepoBrowser>['ref']}
          key={`${repoUrl}:${defaultBranch}:${profileId}`}
          repoUrl={repoUrl}
          initialPath={profileGitPath(profileId)}
          defaultBranch={defaultBranch}
          baseURL={baseURL}
          getToken={getToken}
          author={commitAuthor}
          sessionLabel={profileId}
          onDirtyChange={onDirtyChange}
        />
      </div>

      <Modal
        title="物化预览"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        onOk={handleDeploy}
        okText="确认部署"
        confirmLoading={deployLoading}
        destroyOnHidden
      >
        {previewResult ? (
          <>
            <p>
              将从 <code>{previewResult.ref}</code> 载入 <strong>{previewResult.count}</strong> 个
              Schema key 到 Mongo tenant scope <code>{previewResult.schemaProfile}</code>。
            </p>
            <pre className="schema-profiles-preview-keys">
              {previewResult.keys.join('\n') || '（无 key）'}
            </pre>
          </>
        ) : null}
      </Modal>
    </div>
  );
}

export function SchemaProfilesPage() {
  const gitReady = useDeferredModule(ensureGitFileViewerReady);
  const navigate = useNavigate();
  const { profileId } = useParams<{ profileId?: string }>();
  const { getToken, commitAuthor } = useGitAuth();
  const baseURL = resolveGitApiBaseUrl();
  const {
    settings,
    draft,
    setDraft,
    applyDraft,
    resetDraftToEnvDefaults,
    connectionChanged,
  } = useSchemaGitSettings();

  const [profiles, setProfiles] = useState<SchemaProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [applyingConnection, setApplyingConnection] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gitAccessOk, setGitAccessOk] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const browserRef = useRef<GitRepoBrowserHandle>(null);
  const [, setDirty] = useState(false);
  const { tryLeave } = useGitDraftLeaveGuard(browserRef, {
    confirmDiscard: confirmDiscardDraft,
  });

  const promptCredentials = useCallback(() => {
    setGitAccessOk(false);
    setCredentialsModalOpen(true);
  }, []);

  /** promptCredentials 会将 gitAccessOk=false 并卸载 editor，先 tryLeave */
  const openCredentials = useCallback(async () => {
    const ok = await tryLeave();
    if (!ok) return;
    promptCredentials();
  }, [promptCredentials, tryLeave]);

  /** 拦截 profile 切换：navigate 成功后 key remount 才会发生 */
  const navigateToProfile = useCallback(
    async (id: string) => {
      if (id === profileId) return;
      const ok = await tryLeave();
      if (!ok) return;
      navigate(`/schema-profiles/${id}`);
    },
    [navigate, profileId, tryLeave],
  );

  const loadProfiles = useCallback(
    async (conn: SchemaGitConnection = settings) => {
      if (!conn.repoUrl.trim()) {
        setProfiles([]);
        setGitAccessOk(false);
        setError('请填写 Git 仓库 URL 并点击「应用」');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await schemaProfileService.list(conn);
        if (!result.ok) {
          setProfiles([]);
          setGitAccessOk(false);
          setError(null);
          promptCredentials();
          return;
        }
        setProfiles(result.profiles);
        setGitAccessOk(true);
      } catch (err) {
        if (isGitCredentialNotConfiguredError(err)) {
          setProfiles([]);
          setGitAccessOk(false);
          setError(null);
          promptCredentials();
          return;
        }
        setProfiles([]);
        setGitAccessOk(false);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [promptCredentials, settings],
  );

  const handleApplyConnection = useCallback(async () => {
    const ok = await tryLeave();
    if (!ok) return;
    const next = applyDraft();
    if (!next) {
      message.error('请填写 Git 仓库 URL');
      return;
    }
    setApplyingConnection(true);
    try {
      await loadProfiles(next);
      message.success('已切换 Git 仓库连接');
    } finally {
      setApplyingConnection(false);
    }
  }, [applyDraft, loadProfiles, tryLeave]);

  const handleCredentialsConfigured = useCallback(() => {
    setCredentialsModalOpen(false);
    void loadProfiles();
  }, [loadProfiles]);

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (!gitReady || initialLoadDoneRef.current) {
      return;
    }
    initialLoadDoneRef.current = true;
    void loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gitReady]);

  const columns: ColumnsType<SchemaProfile> = [
    {
      title: 'Profile ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <code>{id}</code>,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            void navigateToProfile(record.id);
          }}
        >
          编辑
        </Button>
      ),
    },
  ];

  if (!gitReady) {
    return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  }

  const editor = !gitAccessOk ? (
    <div className="schema-profiles-editor-placeholder">
      <p>无法访问 Schema Git 仓库，请检查或配置 SSH 凭据后重试。</p>
      <Space>
        <Button type="primary" onClick={() => void openCredentials()}>
          配置 Git 凭据
        </Button>
        <Button onClick={() => void loadProfiles()} loading={loading}>
          重试
        </Button>
      </Space>
    </div>
  ) : profileId ? (
    <SchemaProfileEditor
      key={`${settings.repoUrl}:${settings.branch}:${profileId}`}
      profileId={profileId}
      repoUrl={settings.repoUrl}
      defaultBranch={settings.branch}
      onCredentialRequired={() => {
        void openCredentials();
      }}
      browserRef={browserRef}
      onDirtyChange={setDirty}
    />
  ) : (
    <div className="schema-profiles-editor-placeholder">
      请从左侧选择一个 Schema Profile，或先在 Git 仓库 <code>{SCHEMA_PROFILES_DIR}/</code> 下创建目录。
    </div>
  );

  return (
    <GitSessionRegistryProvider
      baseURL={baseURL}
      getToken={getToken}
      defaultAuthor={commitAuthor}
    >
      <div className="schema-profiles-page">
        <div className="schema-profiles-page-header">
          <div className="schema-profiles-page-header-main">
            <div>
              <h2 className="schema-profiles-page-title">Schema Profile</h2>
              <p className="schema-profiles-page-desc">
                从 Git 仓库 <code>{SCHEMA_PROFILES_DIR}/</code> 自动发现 profile；编辑 JSON
                后提交同步，再执行物化部署。仓库 URL 与分支可在下方修改（保存在本浏览器）。
              </p>
            </div>
            <SchemaGitConnectionForm
              draft={draft}
              onDraftChange={setDraft}
              onApply={() => void handleApplyConnection()}
              onResetEnvDefaults={resetDraftToEnvDefaults}
              connectionChanged={connectionChanged}
              applying={applyingConnection}
              compact
            />
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void loadProfiles()} loading={loading}>
              刷新列表
            </Button>
            <Button onClick={() => void openCredentials()}>Git 凭据</Button>
          </Space>
        </div>

        <div className="schema-profiles-page-body">
          <div className="schema-profiles-page-list">
            {!gitAccessOk && !loading ? (
              <div className="schema-profiles-list-hint">
                <p>无法加载 profile 列表，请配置 Git 凭据或点击重试。</p>
                <Space size="small">
                  <Button type="link" size="small" onClick={() => void openCredentials()}>
                    配置凭据
                  </Button>
                  <Button type="link" size="small" onClick={() => void loadProfiles()}>
                    重试
                  </Button>
                </Space>
              </div>
            ) : error ? (
              <div className="schema-profiles-list-error">{error}</div>
            ) : (
              <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={profiles}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => {
                    void navigateToProfile(record.id);
                  },
                  className:
                    record.id === profileId ? 'schema-profiles-row-selected' : undefined,
                })}
              />
            )}
          </div>
          <div className="schema-profiles-page-editor">{editor}</div>
        </div>
      </div>

      <Modal
        title="配置 Git 凭据"
        open={credentialsModalOpen}
        onCancel={() => setCredentialsModalOpen(false)}
        footer={null}
        width={560}
        destroyOnHidden
      >
        <GitCredentialsSetup
          repoUrl={settings.repoUrl}
          hint={`请保存访问以下仓库的 SSH 私钥：${settings.repoUrl || '（请先填写仓库 URL）'}`}
          onConfigured={handleCredentialsConfigured}
        />
      </Modal>
    </GitSessionRegistryProvider>
  );
}
